import React, { Component, Fragment } from "react";
import Helmet from "react-helmet";
import CaseDetails from "./case-detail";
import FilingTableWithTimeline from "./filing-table-with-timeline";
import ClaimFindingsTable from "./claim-findings-table";
import NotFound from "./not-found";
import LoadingWrapper from "../../common/loading-wrapper";
import ZendeskNewRequestLink from "../../common/links/zendesk-new-request-link";
import { getCaseDetail } from "../../../services/api-ptab";
import { toDateString } from "../../../helpers/converters";
import { PUBLIC_URL } from "../../../config";
import { isArray } from "lodash";

const styles = {
  body: {
    width: "100%",
    padding: 30
  },
  gutter: {
    marginTop: 16
  },
  reportErrorLink: {
    float: "right",
    fontWeight: 400,
    fontSize: "0.7em"
  }
};

class Case extends Component {
  state = {
    caseData: {},
    caseDocketsData: [],
    caseLoading: true,
    caseDocketsLoading: true
  };

  componentDidMount() {
    this._isMounted = true;
    const caseNumber = this.props.params.casenum;
    this._getCase(caseNumber);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const caseNumber = nextProps.params.casenum;
    if (this._isMounted)
      this.setState(
        {
          caseData: {},
          caseDocketsData: []
        },
        () => {
          this._getCase(caseNumber);
        }
      );
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const { caseData, caseLoading, caseDocketsData, caseDocketsLoading } = this.state;
    const caseNumber = this.props.params.casenum;
    const caseType = {
      IPR: "Inter Partes Review",
      CBM: "Covered Business Method",
      PGR: "Post Grant Review",
      DER: "Derivation"
    }[caseNumber.substr(0, 3)];
    const petitionersTitle = isArray(caseData.petitioners) ? `${caseData.petitioners[0]}${caseData.petitioners.length > 1 ? " et al." : ""}` : caseData.petitioners || "";
    const patentOwnersTitle = isArray(caseData.patent_owners) ? `${caseData.patent_owners[0]}${caseData.patent_owners.length > 1 ? " et al." : ""}` : caseData.patent_owners || "";
    const pageTitle = petitionersTitle && patentOwnersTitle ? `${caseNumber} - ${petitionersTitle} v. ${patentOwnersTitle}` : caseNumber;
    let metaKeywords = [
      `${caseNumber}`,
      `${caseType}`,
      `PTAB Case`,
      `PTAB Dockets`,
      `PTAB Litigation`,
      `Lawsuit`,
      `Patent Lawsuit`,
      `Patent Challenge`,
      `Patent Invalidation`,
      `Patent Trial and Appeal Board`,
      `AIA Trial`
    ];
    if (caseData.case_number) {
      metaKeywords = metaKeywords.concat([
        `${caseData.patent ? caseData.patent.patent_number : "No Patent"}`,
        `${patentOwnersTitle}`,
        `${petitionersTitle}`,
        `${caseData.up_status_concat}`,
        `${caseData.ptab_appl_number}`
      ]);
    }
    const helmet = {
      title: pageTitle,
      meta: [
        {
          name: "keywords",
          content: metaKeywords.join(",")
        },
        {
          name: "description",
          content: caseData.case_number
            ? `Case docket and documents for ${caseNumber}, ${caseType} of patent ${caseData.patent.patent_number}, filed at ${toDateString(caseData.filing_date)}.`
            : `Case docket and documents for ${caseNumber}, ${caseType}.`
        }
      ],
      link: [
        {
          rel: "canonical",
          href: encodeURI(PUBLIC_URL + decodeURI(this.props.router.location.pathname))
        }
      ]
    };
    return (
      <div style={styles.body}>
        <Helmet {...helmet} />
        <LoadingWrapper loading={caseLoading || caseDocketsLoading}>
          {caseData.case_number ? (
            <Fragment>
              <h2>
                {caseLoading ? this.props.params.casenum : pageTitle}
                <ZendeskNewRequestLink text="Report Error" subjectPrefix="Correction needed for page" style={styles.reportErrorLink} className="hidden-sm" />
              </h2>
              <div style={styles.gutter}>
                <CaseDetails data={caseData} loading={caseLoading} />
              </div>
              <div style={styles.gutter}>
                <FilingTableWithTimeline data={caseDocketsData} loading={caseDocketsLoading} zipLink={`https://developer.uspto.gov/ptab-api/documents.zip/${caseNumber}/download`} />
              </div>
              <div style={styles.gutter}>
                <ClaimFindingsTable data={caseData.claim_findings} referenceMappings={caseData.reference_mappings} loading={caseLoading} />
              </div>
            </Fragment>
          ) : (
            <NotFound />
          )}
        </LoadingWrapper>
      </div>
    );
  }

  _getCase = caseNumber => {
    if (this._isMounted)
      this.setState({
        caseLoading: true,
        caseDocketsLoading: true
      });
    getCaseDetail(caseNumber)
      .then(json => {
        if (this._isMounted)
          this.setState({
            caseData: {
              ...json._source,
              claim_findings: (json._source.claim_findings || []).map((f, i) => ({
                ...f,
                key: i
              }))
            },
            caseDocketsData: (json._source.filings || []).map((f, i) => ({
              ...f,
              key: i,
              pdf_id: `ptab-filings/${caseNumber}/${f.docket_number}`
            })),
            caseLoading: false,
            caseDocketsLoading: false
          });
      })
      .catch(() => {
        if (this._isMounted)
          this.setState({
            caseData: {},
            caseDocketsData: [],
            caseLoading: false,
            caseDocketsLoading: false
          });
      });
  };
}

export default Case;
